/* eslint-disable react/no-unused-state */
/* eslint-disable sonarjs/no-duplicate-string */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import MarketMarginsModal from './MarketMarginsModal';

import { MARKET_MARGINS_MODAL_TYPES, MODALS } from '../../../constants';

import {
  getActiveModal, getNextNavigationLink,
} from '../../../selectors/common';
import MarketMarginsRow from './MarketMarginsRow';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import SavePromptModal from '../../../components/Modals/SavePromptModal';
import { aSetNavigationInfo, aSetOpenModal } from '../../../reducers/common';

const mapToProps = (state) => ({
  activeModal: getActiveModal(state),
  nextNavigationLink: getNextNavigationLink(state),
});

const actionsToProps = (dispatch) => ({
  setNavigationInfo: (payload) => dispatch(aSetNavigationInfo(payload)),
  setOpenModal: (payload) => dispatch(aSetOpenModal(payload)),
});

class MarketMargins extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialMarketMarginsState: null,
      updatedMarketMarginsState: null,

      showMarketMarginsDetailsModal: false,
      modalType: null,
      marketIdForEdit: null,

      hasChanges: false,
      showCancelModal: false,

      marketsAddedOrEdited: false,
    };
  }

  componentDidMount() {
    this.props.getMargins();
  }

  componentDidUpdate(prevProps, prevState) {
    // need to use JSON.stringify to compare because marketMargins is an array of nested object
    if (JSON.stringify(prevProps.marketMargins) !== JSON.stringify(this.props.marketMargins)) {
      this.setState({
        initialMarketMarginsState: this.props.marketMargins?.marketMargins,
        updatedMarketMarginsState: this.props.marketMargins?.marketMargins,
        hasChanges: false,
      });
    }

    if (!this.state.hasChanges && (this.state.initialMarketMarginsState?.length
      !== this.state.updatedMarketMarginsState?.length || this.marketMarginsHasChanges())
    ) {
      this.setState({
        hasChanges: true,
      });
    }

    if (prevState.hasChanges !== this.state.hasChanges) {
      this.props.setNavigationInfo({ requiresSaveBeforeNavigation: this.state.hasChanges });
    }
  }

  componentWillUnmount() {
    this.props.clearMarketMargins();
  }

  marketMarginsHasChanges = () => this.state.updatedMarketMarginsState
    ?.some((marketMargin) => marketMargin?.hasChanges === true);

  getDefaultMarketMargins = () => this.state.updatedMarketMarginsState?.filter((marketDetails) => marketDetails?.marketId === 'default');

  getMarketMargins = () => this.state.updatedMarketMarginsState?.filter((marketDetails) => marketDetails?.marketId !== 'default');

  toggleMarketMarginsDetailsModal = (modalType, marketId = null) => {
    this.setState({
      showMarketMarginsDetailsModal: !this.state.showMarketMarginsDetailsModal,
      modalType: this.state.modalType ? null : modalType,
      marketIdForEdit:
        (modalType === MARKET_MARGINS_MODAL_TYPES.EDIT && this.state.marketIdForEdit === null)
          ? marketId : null,
    });
  };

  addNewMarketMargin = (newMarketMargin) => {
    const marketMarginsState = [...this.state.updatedMarketMarginsState];
    if (this.state.modalType === MARKET_MARGINS_MODAL_TYPES.EDIT) {
      const marketMarginIdx = marketMarginsState
        ?.findIndex((marketMargin) => marketMargin?.marketId === newMarketMargin?.marketId);
      marketMarginsState[marketMarginIdx] = newMarketMargin;
    } else {
      marketMarginsState.push(newMarketMargin);
    }
    this.setState({
      updatedMarketMarginsState: marketMarginsState,
      marketsAddedOrEdited: true,
    });

    this.toggleMarketMarginsDetailsModal();
  };

  removeMarketMargin = (marketId) => {
    const updatedMarketMarginsState = [...this.state.updatedMarketMarginsState]
      ?.filter((marketMargin) => marketMargin?.marketId !== marketId);

    this.setState({
      updatedMarketMarginsState,
      hasChanges: false,
    });
  };

  getAvailableMarkets = () => {
    const currentSelectedMarketIds = this.state.updatedMarketMarginsState
      ?.map((marketMargin) => marketMargin?.marketId);
    return this.props.allMarkets
      ?.filter((market) => !currentSelectedMarketIds?.includes(market?.id));
  };

  editMarketMargin = (marketId) => {
    this.toggleMarketMarginsDetailsModal(MARKET_MARGINS_MODAL_TYPES.EDIT, marketId);
  };

  promptCancel = () => {
    this.setState({
      showCancelModal: true,
    });
  };

  closePromptModal = () => {
    this.setState({
      showCancelModal: false,
    });
  };

  restoreInitialState = () => {
    this.setState({
      updatedMarketMarginsState: this.state.initialMarketMarginsState,
      hasChanges: false,
      marketsAddedOrEdited: false,
      showCancelModal: false,
    });
  };

  continueWithoutSaving = () => {
    this.props.setOpenModal({ modal: '' });
    this.props.setNavigationInfo({ requiresSaveBeforeNavigation: false });
    this.props.navigate(this.props.nextNavigationLink);
  };

  save = () => {
    this.props.setOpenModal({ modal: '' });

    const updatedMarketMarginsState = [...this.state.updatedMarketMarginsState]
      ?.map((marketMargin) => (
        { ...marketMargin, hasChanges: false }
      ));
    this.props.updateMarketMargins(updatedMarketMarginsState);

    this.setState({
      hasChanges: false,
      marketsAddedOrEdited: false,
    });
  };

  getRemovedMarkets = () => {
    const removedMarketIds = [];

    const initialMarketIds = this.state.initialMarketMarginsState
      ?.map((marketDetails) => marketDetails?.marketId);
    const updatedMarketIds = this.state.updatedMarketMarginsState
      ?.map((marketDetails) => marketDetails?.marketId);

    for (let i = 0; i < initialMarketIds.length; i += 1) {
      if (!updatedMarketIds?.includes(initialMarketIds[i])) {
        removedMarketIds.push(initialMarketIds[i]);
      }
    }

    return removedMarketIds;
  };

  getMarketMarginsModalMessage = () => {
    const messages = [];

    if (this.state.marketsAddedOrEdited) {
      messages.push('- Markets have been added / edited');
    }

    const removedMarkets = this.getRemovedMarkets();
    if (removedMarkets?.length > 0) {
      if (this.state.marketsAddedOrEdited) {
        messages.push('And');
      }
      messages.push('- These market margins have been removed: ');

      for (let i = 0; i < removedMarkets.length; i += 1) {
        const marketName = this.props.allMarkets?.find((market) => market?.id
          === removedMarkets[i])?.name;
        messages.push(`- ${marketName}`);
      }
    }

    return messages;
  };

  render() {
    if (!this.props.marketMargins) return null;
    const defaultMarketMargins = this.getDefaultMarketMargins();
    const marketMargins = this.getMarketMargins();

    return (
      <>
        {this.props.hasPermissionToUpdate && (
          <div className="d-flex">
            <button
              type="button"
              className="btn btn-primary m-t-10 ml-auto"
              onClick={
                this.toggleMarketMarginsDetailsModal.bind(this, MARKET_MARGINS_MODAL_TYPES.ADD)
              }
            >
              Add Market
            </button>
          </div>
        )}

        <div className="table-wrapper m-t-10">
          <div className="table-responsive fix-15">
            <table className="table-style table-style--fixed">
              <thead>
                <tr>
                  <th className="table-style__th-league">Market</th>
                  <th>Margins</th>
                  {defaultMarketMargins?.[0]?.marginOverride && (
                    <>
                      <th className="table-style__th-action">Margins Override</th>
                      <th className="table-style__th-action">
                        Margin Override
                        <br />
                        Probability Threshold
                      </th>
                    </>
                  )}
                  <th className="table-style__th-action"> </th>
                </tr>
              </thead>
              <tbody>
                <MarketMarginsRow
                  marketId="Default"
                  marketMarginsDetails={defaultMarketMargins?.[0]}
                  defaultMarketMargins
                  editMarketMargin={this.editMarketMargin}
                  overallTableHasChanges={this.state.hasChanges}
                  hasPermissionToUpdate={this.props.hasPermissionToUpdate}
                  allMarkets={this.props.allMarkets}
                />

                {marketMargins?.map((market, index) => (
                  <MarketMarginsRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    marketId={market?.marketId}
                    marketMarginsDetails={market}
                    removeMarketMargin={this.removeMarketMargin}
                    editMarketMargin={this.editMarketMargin}
                    overallTableHasChanges={this.state.hasChanges}
                    hasPermissionToUpdate={this.props.hasPermissionToUpdate}
                    allMarkets={this.props.allMarkets}
                  />
                ))}

              </tbody>
            </table>
          </div>
        </div>

        {this.props.hasPermissionToUpdate && (
          <div className="m-t-30 p-r-30 text-right d-flex justify-content-end">
            {this.state.hasChanges && <div className="info-txt">You have unsaved changes !</div>}
            <button
              type="button"
              className="btn btn-secondary m-l-10"
              disabled={!this.state.hasChanges}
              onClick={this.promptCancel}
            >
              Cancel
            </button>
            {' '}
            <button
              type="button"
              className="btn btn-primary m-l-10"
              disabled={!this.state.hasChanges}
              onClick={this.props.setOpenModal.bind(this, { modal: MODALS.MARKET_MARGINS_CHANGE })}
            >
              Save
            </button>
          </div>
        )}

        {this.state.showMarketMarginsDetailsModal && (
          <>
            <div className="overlay-mask overlay-mask--open" />
            <MarketMarginsModal
              closeMarketMarginsModal={this.toggleMarketMarginsDetailsModal}
              modalType={this.state.modalType}
              markets={this.props.allMarkets}
              addNewMarketMargin={this.addNewMarketMargin}
              getAvailableMarkets={this.getAvailableMarkets}
              marketIdForEdit={this.state.marketIdForEdit}
              allMarketMargins={marketMargins}
              defaultMarketMargins={defaultMarketMargins?.[0]}
              isTennis={this.props.isTennis}
            />
          </>
        )}

        {this.state.showCancelModal ? (
          <>
            <div className="overlay-mask overlay-mask--open" />
            <div className="modal modal--open">
              <ConfirmationModal
                modalData={{
                  heading: 'Discard Changes',
                  title: 'Are you sure you want to cancel the updated Market Margins configuration ?',
                  additionalMessages: [],
                }}
                cancel={this.closePromptModal}
                confirmation={this.restoreInitialState}
              />
            </div>
          </>
        ) : null}

        {this.props.activeModal === MODALS.SAVE_BEFORE_NAVIGATION && (
          <SavePromptModal
            modalData={{
              title: 'Do you want to save the changes?',
              additionalMessages: ['Your changes will be lost if you don\'t save them'],
            }}
            cancel={this.continueWithoutSaving}
            confirmation={this.save}
            confirm="Save"
            cancelled="Don't Save"
          />
        )}

        {this.props.activeModal === MODALS.MARKET_MARGINS_CHANGE && (
          <div className="modal modal--open">
            <ConfirmationModal
              modalData={{
                heading: 'Save Changes',
                title: 'Are you sure you want to save the updated Market Margins configuration ?',
                additionalMessages: this.getMarketMarginsModalMessage(),
              }}
              cancel={this.props.setOpenModal.bind(this, { modal: '' })}
              confirmation={this.save}
            />
          </div>
        )}

      </>
    );
  }
}

export default connect(mapToProps, actionsToProps)(MarketMargins);
